import { createTheme } from "@material-ui/core/styles";

export const getTheme = (mode) => {
  const isLight = mode === "light";

  return createTheme({
    palette: {
      type: mode,
      primary: {
        main: "#4F46E5", // Indigo 600 - Modern SaaS primary
        light: "#818CF8",
        dark: "#3730A3",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: isLight ? "#0F172A" : "#F8FAFC", // Slate 900 / Slate 50
        light: isLight ? "#334155" : "#FFFFFF",
        dark: isLight ? "#020617" : "#CBD5E1",
        contrastText: isLight ? "#FFFFFF" : "#0F172A",
      },
      background: {
        default: isLight ? "#F8FAFC" : "#0F172A", // Slate 50 / Slate 900
        paper: isLight ? "#FFFFFF" : "#1E293B", // White / Slate 800
      },
      text: {
        primary: isLight ? "#0F172A" : "#F8FAFC",
        secondary: isLight ? "#64748B" : "#94A3B8",
      },
      error: { main: "#EF4444" },
      warning: { main: "#F59E0B" },
      info: { main: "#3B82F6" },
      success: { main: "#10B981" },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700, fontSize: "2.5rem" },
      h2: { fontWeight: 700, fontSize: "2rem", letterSpacing: "-0.02em" },
      h3: { fontWeight: 600, fontSize: "1.5rem", letterSpacing: "-0.01em" },
      h4: { fontWeight: 600, fontSize: "1.25rem" },
      h5: { fontWeight: 600, fontSize: "1rem" },
      h6: { fontWeight: 600, fontSize: "0.875rem" },
      button: { textTransform: "none", fontWeight: 500, fontSize: "0.875rem" },
      body1: { fontSize: "0.875rem", lineHeight: 1.5 },
      body2: { fontSize: "0.875rem", color: isLight ? "#64748B" : "#94A3B8" },
    },
    shape: {
      borderRadius: 8,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            backgroundColor: isLight ? "#F8FAFC" : "#0F172A",
          },
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: isLight ? '#CBD5E1' : '#475569',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '.Toastify__toast': {
            borderRadius: '8px',
            boxShadow: isLight
              ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
              : '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            backgroundColor: isLight ? '#FFFFFF' : '#1E293B',
            color: isLight ? '#0F172A' : '#F8FAFC',
          },
          '.Toastify__toast-theme--light': {
             backgroundColor: '#FFFFFF',
             color: '#0F172A',
          },
          '.Toastify__toast-theme--dark': {
             backgroundColor: '#1E293B',
             color: '#F8FAFC',
          },
        },
      },
      MuiPaper: {
        root: {
          backgroundImage: "none",
        },
        elevation1: {
          boxShadow: isLight
            ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
            : "0 1px 3px 0 rgba(0, 0, 0, 0.5)",
        },
        elevation2: {
          boxShadow: isLight
            ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.5)",
        },
        rounded: {
          borderRadius: 12,
        },
      },
      MuiButton: {
        root: {
          borderRadius: 8,
          padding: "8px 16px",
          transition: "all 0.2s ease-in-out",
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: isLight
              ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
              : "0 4px 6px -1px rgba(0, 0, 0, 0.5)",
            transform: "translateY(-1px)",
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#4338CA", // Indigo 700
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: 8,
          backgroundColor: isLight ? "#FFFFFF" : "#1E293B",
          transition: "all 0.2s ease-in-out",
          "&:hover $notchedOutline": {
            borderColor: "#818CF8",
          },
          "&$focused $notchedOutline": {
            borderColor: "#4F46E5",
            borderWidth: 2,
            boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.1)",
          },
        },
        notchedOutline: {
          borderColor: isLight ? "#E2E8F0" : "#334155",
        },
      },
      MuiDialog: {
        paper: {
          borderRadius: 16,
          boxShadow: isLight
            ? "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            : "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        },
      },
      MuiDialogTitle: {
        root: {
          padding: "24px 24px 16px 24px",
          "& .MuiTypography-root": {
            fontWeight: 600,
            fontSize: "1.25rem",
          },
        },
      },
      MuiDialogContent: {
        root: {
          padding: "16px 24px",
        },
      },
      MuiDialogActions: {
        root: {
          padding: "16px 24px",
          backgroundColor: isLight ? "#F8FAFC" : "#0F172A",
          borderTop: isLight ? "1px solid #E2E8F0" : "1px solid #1E293B",
        },
      },
      MuiAppBar: {
        root: {
          boxShadow: isLight
            ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
            : "0 1px 3px 0 rgba(0, 0, 0, 0.5)",
        },
        colorDefault: {
          backgroundColor: isLight ? "rgba(255, 255, 255, 0.8)" : "rgba(30, 41, 59, 0.8)",
          backdropFilter: "blur(8px)",
        },
      },
      MuiDrawer: {
        paper: {
          backgroundColor: isLight ? "#FFFFFF" : "#1E293B",
          borderRight: isLight ? "1px solid #E2E8F0" : "1px solid #334155",
        },
      },
      MuiListItem: {
        root: {
          borderRadius: 8,
          margin: "4px 8px",
          width: "calc(100% - 16px)",
          paddingTop: 8,
          paddingBottom: 8,
          "&$selected": {
            backgroundColor: isLight ? "#EEF2FF" : "#312E81",
            color: isLight ? "#4F46E5" : "#A5B4FC",
            "&:hover": {
              backgroundColor: isLight ? "#E0E7FF" : "#3730A3",
            },
            "& .MuiListItemIcon-root": {
              color: isLight ? "#4F46E5" : "#A5B4FC",
            },
          },
        },
        button: {
          "&:hover": {
            backgroundColor: isLight ? "#F1F5F9" : "#334155",
            color: isLight ? "#0F172A" : "#F8FAFC",
            "& .MuiListItemIcon-root": {
              color: isLight ? "#4F46E5" : "#A5B4FC",
            },
          },
        },
      },
      MuiListItemIcon: {
        root: {
          minWidth: 40,
          color: isLight ? "#64748B" : "#94A3B8",
          transition: "color 0.2s",
        },
      },
      MuiListSubheader: {
        root: {
          backgroundColor: "transparent",
          color: isLight ? "#64748B" : "#94A3B8",
          fontSize: "0.75rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          lineHeight: "2.5",
          paddingLeft: 24,
        },
        inset: {
          paddingLeft: 24,
        }
      },
      MuiInputBase: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 1000px ${isLight ? "#FFFFFF" : "#1E293B"} inset !important`,
            WebkitTextFillColor: `${isLight ? "#0F172A" : "#F8FAFC"} !important`,
            caretColor: isLight ? "#0F172A" : "#F8FAFC",
            borderRadius: "inherit",
            transition: "background-color 99999s ease-in-out 0s, color 99999s ease-in-out 0s",
          },
          "&:-webkit-autofill:hover": {
            WebkitBoxShadow: `0 0 0 1000px ${isLight ? "#FFFFFF" : "#1E293B"} inset !important`,
          },
          "&:-webkit-autofill:focus": {
            WebkitBoxShadow: `0 0 0 1000px ${isLight ? "#FFFFFF" : "#1E293B"} inset !important`,
          },
          "&:-webkit-autofill:active": {
            WebkitBoxShadow: `0 0 0 1000px ${isLight ? "#FFFFFF" : "#1E293B"} inset !important`,
          },
        },
      },
    },
  });
};
